/**
 * Render a timestamp as an Arabic relative label ("منذ 3 ساعات").
 *
 * Uses `Intl.RelativeTimeFormat` so plural rules and dual forms come from the
 * platform rather than hand-rolled string logic — Arabic has six plural
 * categories, which is exactly the kind of thing not to reimplement.
 */
const DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime()) || Number.isNaN(now.getTime())) return '';

  const formatter = new Intl.RelativeTimeFormat('ar', { numeric: 'auto' });
  let duration = (then.getTime() - now.getTime()) / 1000;

  for (const division of DIVISIONS) {
    // Test the ROUNDED value, not the raw one. Checking `duration` first lets
    // 59.6 seconds into the "second" bucket and then rounds it to 60, printing
    // "60 ثانية" instead of "منذ دقيقة" — and, worst of all, renders every
    // timestamp from ~350 to 365 days old as "12 شهرًا" rather than "العام الماضي".
    const rounded = Math.round(duration);
    if (Math.abs(rounded) < division.amount) {
      return formatter.format(rounded, division.unit);
    }
    duration /= division.amount;
  }

  return formatter.format(Math.round(duration), 'year');
}

/**
 * Display label for a post: a fixed label for seeded content, otherwise a
 * relative time derived from the stored timestamp.
 */
export function postTimestamp(
  post: { createdAt: string | null; label?: string | undefined },
  now?: Date,
): string {
  if (post.label) return post.label;
  if (!post.createdAt) return '';
  return formatRelativeTime(post.createdAt, now);
}

/** Arabic pluralisation for the reply counter under each question. */
export function replyCountLabel(count: number): string {
  if (count === 0) return 'بلا ردود';
  if (count === 1) return 'رد واحد';
  if (count === 2) return 'ردّان';
  if (count <= 10) return `${count} ردود`;
  return `${count} ردًا`;
}

/** Collision-resistant id without pulling in a uuid dependency. */
export function createId(prefix: string): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) return `${prefix}-${cryptoApi.randomUUID()}`;
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
